/**
 * Stock-state styling — shared by the marketplace browse card (`ProductCard`),
 * the chat product card (`ChatProductCards`), and the product-detail page.
 * Color is NEVER the only signal: callers pair the class with a visible text
 * label AND an `aria-label` (WCAG — no color-only meaning). Tokens come from
 * the `--g-*` Tailwind mirror.
 *
 * ONE vocabulary lives here.
 *
 * `STOCK_PILL_CLASS` — the original filled pill — was kept for a while because
 * the chat product card still imported it and chat was being rebuilt in
 * parallel. Chat now uses `STOCK_MARK` too, so the pill had no call sites left
 * and has been deleted rather than left as a second way to say the same thing.
 *
 *   STOCK_MARK — the counter-slip vocabulary (2026-08). Availability on a
 *   catalogue card is META, not a badge: a filled/hollow DOT carries the state
 *   and the label sits in ordinary meta ink. Two reasons this replaced the
 *   pill on the browse grid. First, `--g-success` (#3F8E5A) as 11px TEXT on
 *   card white is ~3.5:1 — under the 4.5:1 floor — so the old pill was quietly
 *   unreadable; moving the colour onto a dot and the text to `--g-ink-2` fixes
 *   the contrast without losing the signal. Second, a green pill under every
 *   single price makes "in stock" the loudest thing on a card whose actual
 *   payload is the price and the stall. The state is still carried on THREE
 *   channels — dot fill (solid vs hollow), dot colour, and the text label — so
 *   it survives both colour-blindness and a monochrome screen.
 */

/** Known stock states (mirror backend PRODUCT_STOCK_STATE_VALUES). */
export type KnownStockState = "in_stock" | "low" | "out_of_stock" | "unknown";

/** Dot + label classes for the quiet counter-slip availability mark. */
export interface StockMarkStyle {
  /** The 6px state dot: filled for stocked states, hollow for absent/unknown. */
  dot: string;
  /** The label ink. Never a semantic colour — always a contrast-safe ink. */
  label: string;
}

export const STOCK_MARK: Record<KnownStockState, StockMarkStyle> = {
  in_stock: {
    dot: "bg-[color:var(--g-success)]",
    label: "text-[color:var(--g-ink-2)]",
  },
  // "Low" is an attention state: solid dot, and the label carries full ink
  // weight so it reads a step louder than plain availability.
  low: {
    dot: "bg-[color:var(--g-warn)]",
    label: "text-[color:var(--g-ink)]",
  },
  // Absent states go HOLLOW — the shape change is the non-colour channel.
  // The LABEL stays at `--g-ink-2` even though these states are de-emphasised:
  // `--g-ink-3` computes to ~2.4:1 on a light card, and "out of stock" is
  // precisely the word a buyer must not fail to read. De-emphasis is carried by
  // the hollow dot, not by making the text harder to see.
  out_of_stock: {
    dot: "border border-[color:var(--g-error)] bg-transparent",
    label: "text-[color:var(--g-ink-2)]",
  },
  unknown: {
    dot: "border border-[color:var(--g-rule-strong)] bg-transparent",
    label: "text-[color:var(--g-ink-2)]",
  },
};

/** Coerce an arbitrary server stock string to a known state (default: unknown). */
export function normalizeStockState(state: string): KnownStockState {
  if (
    state === "in_stock" ||
    state === "low" ||
    state === "out_of_stock" ||
    state === "unknown"
  ) {
    return state;
  }
  return "unknown";
}
